import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, Loader2 } from 'lucide-react';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { AuthShell } from '../components/auth/AuthShell';
import { EmailLinkConsent } from '../components/auth/EmailLinkConsent';
import { Button } from '../components/ui/button';
import { authErrorMessage, clearPendingEmailLink, readPendingEmailLink, safeAuthDestination, signInPath } from '../utils/authNavigation';

const stateMessages = {
  expired: 'This sign-in link has expired.',
  used: 'This sign-in link has already been used.',
  invalid: 'This link is invalid or has been replaced by a newer one.',
  unavailable: 'Email sign-in is unavailable for this account. Use password sign-in or contact support.',
};

export default function EmailSignIn() {
  const [pending] = useState(readPendingEmailLink);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [terms, setTerms] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const [switchAccount, setSwitchAccount] = useState(false);
  const [missingAfterSignIn, setMissingAfterSignIn] = useState(false);
  const [retry, setRetry] = useState(0);
  const { user, loading: checkingSession, confirmEmailLink } = useAuth();
  const navigate = useNavigate();
  const completing = useRef(false);
  const destination = safeAuthDestination(preview?.destination && preview.state !== 'invalid' ? preview.destination : pending.destination);

  useEffect(() => {
    if (checkingSession || completing.current) return;
    const controller = new AbortController();
    setLoading(true); setError(''); setSwitchAccount(false);
    if (!pending.token) { setPreview({ state: 'invalid' }); setLoading(false); return; }
    api.post('/auth/magic-link/preview', { token: pending.token }, { signal: controller.signal })
      .then(({ data }) => setPreview(data))
      .catch(err => { if (!controller.signal.aborted) setError(authErrorMessage(err, 'Could not check this link. Please try again.')); })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [pending.token, user?.id, checkingSession, retry]);

  const confirm = async event => {
    event.preventDefault();
    if (busy) return;
    setBusy(true); setError(''); completing.current = true;
    try {
      const data = await confirmEmailLink({ token: pending.token, accepted_terms: terms, marketing_opt_in: marketing,
        switch_confirmed: switchAccount, switch_from_user_id: preview.current_user_id });
      clearPendingEmailLink();
      if (data.question_missing) setMissingAfterSignIn(true);
      else navigate(safeAuthDestination(data.destination), { replace: true });
    } catch (err) {
      completing.current = false;
      setError(authErrorMessage(err, 'Unable to finish sign-in. Please request another link.'));
      if (err.response?.data?.detail?.state) setPreview(prev => ({ ...prev, state: err.response.data.detail.state }));
      if (err.response?.status === 409) setRetry(value => value + 1);
    } finally { setBusy(false); }
  };

  return <AuthShell title={missingAfterSignIn ? 'You’re signed in' : 'Continue to PharmaConsensus'} description="Your email. Your predictions.">
    {(loading || checkingSession) && <p className="flex items-center gap-2 text-slate-300" role="status" data-testid="magic-confirm-loading"><Loader2 className="w-4 h-4 animate-spin" />Checking your sign-in link…</p>}
    {missingAfterSignIn ? <p className="text-slate-300 mb-6" data-testid="magic-question-deleted-message">That question is no longer available. Your account is ready for the next one.</p> : !loading && !checkingSession && preview?.state === 'valid' ? (
      <form onSubmit={confirm} data-testid="magic-confirm-form">
        <p className="text-sm text-slate-400">Sign in as</p>
        <p className="font-semibold text-white break-words mt-1 mb-6" data-testid="magic-confirm-email">{preview.email}</p>
        {preview.question_title && <p className="text-slate-300 border-l-2 border-indigo-400 pl-4 mb-6" data-testid="magic-selected-question">{preview.question_title}</p>}
        {preview.question_missing && <p className="text-amber-300 text-sm mb-5" data-testid="magic-question-missing-notice">This question is no longer available. You can still sign in and browse other events.</p>}
        {preview.is_new_user && <EmailLinkConsent terms={terms} setTerms={setTerms} marketing={marketing} setMarketing={setMarketing} />}
        {preview.switch_required && <label className="flex items-start gap-3 text-sm text-amber-200 mb-6 break-words" data-testid="magic-switch-account-label">
          <input type="checkbox" checked={switchAccount} onChange={event => setSwitchAccount(event.target.checked)} className="mt-1 h-5 w-5 shrink-0 accent-indigo-500" required data-testid="magic-switch-account-checkbox" />
          <span className="min-w-0">Switch from <strong>{preview.current_email}</strong> to <strong>{preview.email}</strong> on this browser.</span>
        </label>}
        <Button type="submit" disabled={busy || (preview.is_new_user && !terms) || (preview.switch_required && !switchAccount)} className="w-full h-auto min-h-12 py-3 whitespace-normal rounded-full bg-indigo-600 hover:bg-indigo-500" data-testid="magic-confirm-submit-button">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}{busy ? 'Signing in…' : preview.question_title ? 'Continue to question' : 'Continue to events'}
        </Button>
        <p className="text-xs text-slate-400 mt-3" data-testid="magic-no-automatic-vote-note">Signing in does not cast a vote.</p>
      </form>
    ) : !loading && !checkingSession && preview && <p role="status" className="text-slate-300 mb-6" data-testid="magic-link-status-message">{stateMessages[preview.state]}</p>}
    {error && <p role="alert" className="text-sm text-rose-300 mt-5" data-testid="magic-confirm-error">{error}</p>}
    {!loading && !checkingSession && !preview && <Button variant="outline" className="mt-4" onClick={() => setRetry(value => value + 1)} data-testid="magic-preview-retry-button">Try again</Button>}
    {!missingAfterSignIn && !loading && !checkingSession && <Link to={signInPath(destination)} onClick={clearPendingEmailLink} className="block text-indigo-300 hover:text-indigo-200 text-sm mt-6" data-testid="magic-request-new-link">Request a new sign-in link</Link>}
    {preview?.state === 'unavailable' && <Link to={`${signInPath(destination)}&method=password`} className="block text-indigo-300 mt-4 text-sm" data-testid="magic-use-password-link">Use password sign-in</Link>}
    <Link to="/events" className="inline-block text-slate-300 hover:text-white mt-6 text-sm" data-testid="magic-back-to-events">Browse events →</Link>
  </AuthShell>;
}